import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { Box, rem, useColorModeValue, Flex, Text } from '@vimeo/bokeh';
import { TrashBin } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { CdnAvatar } from '@vimeo/live-core/components/avatar';
import { BokehIconButton, BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { vimeoConfig } from '@vimeo/live-core/configs/VimeoConfig';
import { IQnAQuestionReply } from '@vimeo/live-core/types/interaction_tools';
import { stopEventPropagation } from '@vimeo/live-core/utils/dom/event';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';
import {
  createAvatarWrapperStyle,
  createQuestionTextStyle,
  createQuestionInfoStyle,
  TIME_ASKED_STYLE,
} from './QuestionItem.styles';
import { QuestionItemAuthor } from './QuestionItemAuthor';
import { useQuestionTimeAsked } from './useQuestionTimeAsked';

interface IQuestionItemReplyProps extends IBaseDomElementProps {
  reply: IQnAQuestionReply;
  isManagementAccessed?: boolean;
  onDelete?: (questionReplyId: string) => void;
}

export function NewQuestionItemReply({
  className = createInteractionDomName('question-item-reply'),
  reply,
  isManagementAccessed = false,
  onDelete,
}: IQuestionItemReplyProps) {
  const [isItemHovered, setItemHovered] = useState<boolean>(false);

  const { user, createdAt, text } = reply;
  const avatar: string =
    !user || !user.avatarUrl ? vimeoConfig.USER.DEFAULT_LOGO_URL(75) : user.avatarUrl;
  const timeAsked: string = useQuestionTimeAsked(createdAt);
  const name: string = !user || !user.displayName ? translations.anonymous : user.displayName;

  const itemReplyHoverBackground = useColorModeValue('slate.100', 'grayscale.700');

  const itemReplyHoverStyle = useMemo(
    () =>
      isManagementAccessed
        ? {
            borderRadius: 'sm',
            borderColor: 'transparent',
            background: itemReplyHoverBackground,
          }
        : {},
    [isManagementAccessed, itemReplyHoverBackground],
  );

  const onMouseEnter = useCallback(() => {
    if (isManagementAccessed) {
      setItemHovered(true);
    }
  }, [isManagementAccessed]);

  const onMouseLeave = useCallback(() => {
    if (isManagementAccessed) {
      setItemHovered(false);
    }
  }, [isManagementAccessed]);

  const onDeleteReplyClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (onDelete) {
        return onDelete(reply.id);
      }
    },
    [onDelete, reply],
  );

  return (
    <Box
      className={className}
      position={'relative'}
      padding={rem(8)}
      borderLeft={`solid ${rem(1)}`}
      borderColor={'stroke'}
      width={'100%'}
      sx={isItemHovered ? itemReplyHoverStyle : {}}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {onDelete && isItemHovered ? (
        <Flex
          position={'absolute'}
          right={rem(8)}
          top={rem(8)}
          zIndex={1}
          onMouseDown={stopEventPropagation}
          onTouchStart={stopEventPropagation}
        >
          <BokehTooltip placement={'top'} label={translations.delete}>
            <BokehIconButton
              className={createInteractionDomName(className, 'delete')}
              size={'xs'}
              icon={<TrashBin />}
              width={rem(24)}
              onClick={onDeleteReplyClick}
            />
          </BokehTooltip>
        </Flex>
      ) : null}

      <Flex
        position={'relative'}
        width={'100%'}
        minWidth={0}
        flexDirection={'row'}
        alignItems={'flex-start'}
        gap={rem(8)}
        marginBottom={0}
      >
        <Box sx={createAvatarWrapperStyle()}>
          <CdnAvatar
            className={createInteractionDomName(className, 'author-avatar')}
            size={'sm'}
            src={avatar}
          />
        </Box>

        <Flex
          flexDirection={'column'}
          alignItems={'flex-start'}
          width={'100%'}
          gap={rem(8)}
          overflow={'hidden'}
        >
          <Flex
            sx={{
              ...createQuestionInfoStyle(),
              paddingRight: rem(24),
            }}
          >
            <QuestionItemAuthor
              className={createInteractionDomName(className, 'author-name')}
              name={name}
            />

            <Text
              className={createInteractionDomName(className, 'time')}
              variant={'body-sm'}
              sx={TIME_ASKED_STYLE}
            >
              {timeAsked}
            </Text>
          </Flex>

          <Text
            className={createInteractionDomName(className, 'text')}
            variant={'body-md'}
            sx={createQuestionTextStyle()}
          >
            {text}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}
